import {Component, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../base-modal/base-modal.component";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {BloggerProfileDialogData} from "../types/blogger-modal.types";
import {FormGroup} from "@angular/forms";
import {RecordData, RecordTableColumnParams} from "../../../../types/records-table.types";
import {CommonModule} from "@angular/common";
import {BloggerProfileService} from "../services/blogger-profile.service";
import {bloggerModalResourceType} from "../configs/blogger-modal-config";
import {defaultRecordTableParams} from "../../../../models/default-record-table-params";
import {bloggerProfileModalName, fieldsToReset} from "../configs/blogger-card-modal-config";
import {LoaderComponent} from "../../../../../shared/components/loader/loader.component";
import {
  CurrentBloggerProfileModalComponent
} from "./components/current-blogger-profile-modal/current-blogger-profile-modal.component";
import {
  NewBloggerProfileModalComponent
} from "./components/new-blogger-profile-modal/new-blogger-profile-modal.component";
import {defaultRecordData} from "../../../../models/default-record-data";

@Component({
  selector: 'app-blogger-profile-modal',
  standalone: true,
  imports: [
    CommonModule,
    BaseModalComponent,
    LoaderComponent,
    CurrentBloggerProfileModalComponent,
    NewBloggerProfileModalComponent
  ],
  templateUrl: './blogger-profile-modal.component.html',
  styleUrl: './blogger-profile-modal.component.scss'
})
export class BloggerProfileModalComponent extends BaseModalComponent {
  public bloggerDialogData = inject<BloggerProfileDialogData>(MAT_DIALOG_DATA);

  private bloggerProfileService: BloggerProfileService = inject(BloggerProfileService);

  public readonly defaultRecordData = defaultRecordData;
  public hasBlogger = signal<boolean>(false);
  public bloggerCardHeader = signal<RecordData | null>(null);
  public bloggerPropertiesParams = signal<Array<RecordTableColumnParams>>([]);
  public bloggerFormGroup: FormGroup = new FormGroup({});

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.modalStatesService.openModal(bloggerProfileModalName);
    this.hasBlogger.set(!!this.bloggerDialogData.bloggerId);
    if (!this.hasBlogger()) {
      this.bloggerFormGroup = this.bloggerProfileService.initForm(this.bloggerDialogData)
    } else {
      await this.getMetaBloggerProperties();
    }
  }

  public override ngOnDestroy() {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(bloggerProfileModalName);
    this.updateRecordData();
  }

  public initBloggerCardHeader(bloggerData: RecordData | null): void  {
    if (!bloggerData) {
      this.resetBloggerCard();
      return;
    }

    const bloggerAttachmentDate = this.bloggerDialogData.recordData['bloggerAttachmentDate'];
    this.hasBlogger.set(!!bloggerData.id);
    this.bloggerDialogData.bloggerId = Number(bloggerData.id);
    this.bloggerCardHeader.set(bloggerData);
    this.bloggerDialogData.recordData['blogger'] = this.bloggerProfileService.updateBloggerData(this.bloggerDialogData.recordData, Number(bloggerData.id));
    this.bloggerDialogData.recordData['bloggerAttachmentDate'] = bloggerAttachmentDate ?
      bloggerAttachmentDate :
      new Date().toLocaleDateString();
  }

  public toggleLoadingState(isLoading: boolean): void {
    this.loadingService.setIsLoadingState(isLoading);
  }

  private async getMetaBloggerProperties(): Promise<void> {
    const data = await this.getRecordTableParams(bloggerModalResourceType.path)
      .catch(() => defaultRecordTableParams.fields);
    this.bloggerPropertiesParams.set(data);
  }

  private resetBloggerCard(): void {
    this.hasBlogger.set(false);
    this.bloggerCardHeader.set(null);
    this.bloggerDialogData = this.bloggerProfileService.resetDialogDataField(this.bloggerDialogData);
    this.bloggerFormGroup = this.bloggerProfileService.initForm(this.bloggerDialogData);
    this.bloggerDialogData.recordData['blogger'] = this.bloggerProfileService.updateBloggerData(this.bloggerDialogData.recordData, null);
    this.bloggerDialogData.recordData['bloggerAttachmentDate'] = null;
  }

  private updateRecordData(): void {
    const bloggerId = (this.bloggerDialogData.recordData['blogger'] as RecordData).id;
    this.bloggerDialogData.recordData['blogger'] = this.bloggerProfileService.updateBloggerData(this.bloggerDialogData.recordData, Number(bloggerId));
    fieldsToReset.forEach(key => {
      this.bloggerDialogData.recordData[key] = {
        ...this.bloggerDialogData.recordData[key] as RecordData,
        bloggerId: bloggerId
      };
    });
  }
}
